import {
  type AtualizarUsuarioInterno,
  type CodigoDePapel,
  type CriarUsuarioInterno,
  type StatusDoUsuario,
  codigoDePapelSchema,
  statusDoUsuarioSchema,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet, apiPatch, apiPost } from '@/lib/api'
import { ROTAS_DE_USUARIOS } from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

/**
 * Usuários — leitura e escrita.
 *
 * O modelo de LEITURA vive aqui, e não em `@locaveri/shared`, porque ele é a
 * projeção que a API devolve (sem hash de senha, sem segredo de TOTP, com o
 * papel já resolvido) — não é o contrato de entrada, que é o que o pacote
 * compartilhado guarda. Misturar os dois faria o formulário herdar campos que
 * ele nunca envia.
 */

export const usuarioSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  email: z.string(),
  telefone: z.string().nullable(),
  status: statusDoUsuarioSchema,
  papeis: z.array(codigoDePapelSchema),
  emailVerificado: z.boolean(),
  /** `true` quando o segundo fator já está ativo para este usuário. */
  totpAtivo: z.boolean(),
  /** `null` para a equipe interna; preenchido para condutor de uma conta. */
  accountId: z.uuid().nullable(),
  centroDeCusto: z.string().nullable(),
  ultimoLoginEm: z.string().nullable(),
  criadoEm: z.string(),
})

export type Usuario = z.infer<typeof usuarioSchema>

export const paginaDeUsuariosSchema = z.object({
  itens: z.array(usuarioSchema),
  total: z.number().int().nonnegative(),
  pagina: z.number().int().positive(),
  tamanho: z.number().int().positive(),
})

export type PaginaDeUsuarios = z.infer<typeof paginaDeUsuariosSchema>

export interface FiltrosDeUsuarios {
  readonly pagina: number
  readonly tamanho: number
  readonly busca: string
  readonly status: StatusDoUsuario | ''
  readonly papel: CodigoDePapel | ''
  /** `true` lista a equipe interna; `false`, os condutores das contas. */
  readonly apenasInternos: boolean
  /** Restringe a uma conta (usado no vínculo de condutores da PJ). */
  readonly accountId?: string | undefined
}

export const CHAVE_DE_USUARIOS = 'usuarios'

export function useUsuarios(filtros: FiltrosDeUsuarios): UseQueryResult<PaginaDeUsuarios> {
  return useQuery({
    queryKey: [CHAVE_DE_USUARIOS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        busca: filtros.busca,
        status: filtros.status,
        papel: filtros.papel,
        apenasInternos: filtros.apenasInternos,
        accountId: filtros.accountId,
      })

      return apiGet(`${ROTAS_DE_USUARIOS.lista}${query}`, paginaDeUsuariosSchema, { signal })
    },
    // Mantém a página anterior enquanto a nova carrega: sem isso a tabela
    // pisca em branco a cada tecla digitada no filtro.
    placeholderData: (anterior) => anterior,
  })
}

export function useCriarUsuario(): UseMutationResult<Usuario, Error, CriarUsuarioInterno> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CriarUsuarioInterno) =>
      apiPost(ROTAS_DE_USUARIOS.lista, usuarioSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_USUARIOS] })
    },
  })
}

export interface AtualizacaoDeUsuario {
  readonly id: string
  readonly dados: AtualizarUsuarioInterno
}

export function useAtualizarUsuario(): UseMutationResult<Usuario, Error, AtualizacaoDeUsuario> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeUsuario) =>
      apiPatch(ROTAS_DE_USUARIOS.porId(id), usuarioSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_USUARIOS] })
    },
  })
}

/**
 * Reenvia o convite (o link de definição de senha).
 *
 * Nada aqui devolve o token: ele vai por e-mail e só existe hasheado no banco.
 * Um painel que mostrasse o link do convite estaria distribuindo credencial de
 * terceiro pela tela de quem cadastra.
 */
export function useReenviarConvite(): UseMutationResult<unknown, Error, string> {
  return useMutation({
    mutationFn: (id: string) => apiPost(ROTAS_DE_USUARIOS.convite(id), z.unknown(), {}),
  })
}
